import React, { useState } from 'react';
import { XIcon, ArrowRightIcon, SparklesIcon, BookOpenIcon } from 'lucide-react';
import { FUNNEL_TYPES } from '../lib/data';
import { Button } from './ui/Button';
interface QuizRecommenderProps {
  isOpen: boolean;
  onClose: () => void;
  onRecommend: (typeId: string) => void;
}
export function QuizRecommender({
  isOpen,
  onClose,
  onRecommend
}: QuizRecommenderProps) {
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({
    goal: '',
    price: '',
    exp: ''
  });
  if (!isOpen) return null;
  const handleAnswer = (question: keyof typeof answers, value: string) => {
    setAnswers(prev => ({
      ...prev,
      [question]: value
    }));
    if (value === 'learn') {
      // Skip to learn-all result
      setAnswers(prev => ({
        ...prev,
        goal: 'learn'
      }));
      setStep(4);
      return;
    }
    if (step < 3) {
      setStep(step + 1);
    } else {
      setStep(4);
    }
  };
  const getRecommendation = () => {
    if (answers.goal === 'learn') return 'learn-all';
    if (answers.goal === 'list') return 'lead';
    if (answers.goal === 'product') return 'product';
    if (answers.goal === 'webinar') return 'presentation';
    if (answers.goal === 'appointments') return 'appointment';
    if (answers.goal === 'challenge') return 'challenge';
    return 'lead';
  };
  const resetAndClose = () => {
    setStep(1);
    setAnswers({
      goal: '',
      price: '',
      exp: ''
    });
    onClose();
  };
  const applyRecommendation = () => {
    onRecommend(getRecommendation());
    resetAndClose();
  };
  const isLearnMode = answers.goal === 'learn';
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-in fade-in duration-200" data-id="element-132">
      <div className="bg-background rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh] border border-border" data-id="element-133">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border" data-id="element-134">
          <div className="flex items-center gap-2 text-blue-600 font-semibold" data-id="element-135">
            <SparklesIcon className="w-5 h-5" data-id="element-136" />
            Find Your Template
          </div>
          <button onClick={resetAndClose} className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-full transition-colors" data-id="element-137">
            <XIcon className="w-5 h-5" data-id="element-138" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8 overflow-y-auto" data-id="element-139">
          {step === 1 && <div className="animate-in slide-in-from-right-4 fade-in" data-id="element-140">
              <h2 className="text-2xl font-bold text-foreground mb-2" data-id="element-141">
                What do you want to do right now?
              </h2>
              <p className="text-muted-foreground mb-6" data-id="element-142">
                Tell us your main goal so we can point you in the right
                direction.
              </p>

              <div className="space-y-3" data-id="element-143">
                {[{
              id: 'list',
              label: 'Collect emails from people'
            }, {
              id: 'product',
              label: 'Sell a product online'
            }, {
              id: 'appointments',
              label: 'Get people to book a call with me'
            }, {
              id: 'webinar',
              label: 'Host an online video presentation'
            }, {
              id: 'challenge',
              label: 'Run a fun multi-day challenge'
            }].map(opt => <button key={opt.id} onClick={() => handleAnswer('goal', opt.id)} className="w-full text-left p-4 rounded-xl border-2 border-border hover:border-primary hover:bg-primary/5 transition-all font-medium text-foreground hover:text-primary flex justify-between items-center group" data-id="element-144">
                    {opt.label}
                    <ArrowRightIcon className="w-4 h-4 opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all" data-id="element-145" />
                  </button>)}

                {/* Learn about everything option */}
                <div className="pt-2 border-t border-border mt-2" data-id="element-146">
                  <button onClick={() => handleAnswer('goal', 'learn')} className="w-full text-left p-4 rounded-xl border-2 border-blue-100 bg-blue-50/50 hover:border-blue-500 hover:bg-blue-50 transition-all font-medium text-blue-700 hover:text-blue-800 flex justify-between items-center group" data-id="element-147">
                    <span className="flex items-center gap-2" data-id="element-148">
                      <BookOpenIcon className="w-4 h-4" data-id="element-149" />I want to learn more
                      about funnel types
                    </span>
                    <ArrowRightIcon className="w-4 h-4 opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all" data-id="element-150" />
                  </button>
                </div>
              </div>
            </div>}

          {step === 2 && <div className="animate-in slide-in-from-right-4 fade-in" data-id="element-151">
              <h2 className="text-2xl font-bold text-foreground mb-2" data-id="element-152">
                How much does your thing cost?
              </h2>
              <p className="text-muted-foreground mb-6" data-id="element-153">
                Selling something expensive takes a little more explaining than
                selling something cheap.
              </p>

              <div className="space-y-3" data-id="element-154">
                {[{
              id: 'free',
              label: "It's totally free"
            }, {
              id: 'low',
              label: 'Under $50 (A quick buy)'
            }, {
              id: 'mid',
              label: '$50 - $500 (Takes some thinking)'
            }, {
              id: 'high',
              label: 'Over $500 (A big decision)'
            }].map(opt => <button key={opt.id} onClick={() => handleAnswer('price', opt.id)} className="w-full text-left p-4 rounded-xl border-2 border-border hover:border-primary hover:bg-primary/5 transition-all font-medium text-foreground hover:text-primary flex justify-between items-center group" data-id="element-155">
                    {opt.label}
                    <ArrowRightIcon className="w-4 h-4 opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all" data-id="element-156" />
                  </button>)}
              </div>
            </div>}

          {step === 3 && <div className="animate-in slide-in-from-right-4 fade-in" data-id="element-157">
              <h2 className="text-2xl font-bold text-foreground mb-2" data-id="element-158">
                Have you ever built a website before?
              </h2>
              <p className="text-muted-foreground mb-6" data-id="element-159">
                We'll make sure to show you templates that match your skill
                level.
              </p>

              <div className="space-y-3" data-id="element-160">
                {[{
              id: 'new',
              label: "Nope, I'm brand new to this"
            }, {
              id: 'some',
              label: "I've made a few simple pages"
            }, {
              id: 'pro',
              label: 'I do this all the time'
            }].map(opt => <button key={opt.id} onClick={() => handleAnswer('exp', opt.id)} className="w-full text-left p-4 rounded-xl border-2 border-border hover:border-primary hover:bg-primary/5 transition-all font-medium text-foreground hover:text-primary flex justify-between items-center group" data-id="element-161">
                    {opt.label}
                    <ArrowRightIcon className="w-4 h-4 opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all" data-id="element-162" />
                  </button>)}
              </div>
            </div>}

          {step === 4 && <div className="animate-in zoom-in-95 fade-in text-center py-8" data-id="element-163">
              <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-164">
                {isLearnMode ? <BookOpenIcon className="w-8 h-8" data-id="element-165" /> : <SparklesIcon className="w-8 h-8" data-id="element-166" />}
              </div>

              {isLearnMode ? <>
                  <h2 className="text-2xl font-bold text-foreground mb-2" data-id="element-167">
                    Great, let's explore!
                  </h2>
                  <p className="text-muted-foreground mb-8 max-w-sm mx-auto" data-id="element-168">
                    We'll open up the learning cards so you can click through
                    each funnel type and see how they work. Take your time!
                  </p>
                  <Button size="lg" className="w-full text-lg" onClick={applyRecommendation} data-id="element-169">
                    Start Learning
                  </Button>
                </> : <>
                  <h2 className="text-2xl font-bold text-foreground mb-2" data-id="element-170">
                    We found your perfect match!
                  </h2>
                  <p className="text-muted-foreground mb-8 max-w-sm mx-auto" data-id="element-171">
                    Based on what you told us, you should start with a{' '}
                    <strong className="text-foreground" data-id="element-172">
                      {FUNNEL_TYPES.find(t => t.id === getRecommendation())?.name.toUpperCase()}
                    </strong>{' '}
                    template. It's exactly what you need to get started.
                  </p>
                  <Button size="lg" className="w-full text-lg" onClick={applyRecommendation} data-id="element-173">
                    Show Me Templates
                  </Button>
                </>}

              <button onClick={resetAndClose} className="mt-4 text-sm text-muted-foreground hover:text-foreground font-medium" data-id="element-174">
                Skip and browse all
              </button>
            </div>}
        </div>

        {/* Progress Bar */}
        {step < 4 && <div className="bg-muted/50 p-4 border-t border-border" data-id="element-175">
            <div className="flex justify-between text-xs font-medium text-muted-foreground mb-2" data-id="element-176">
              <span data-id="element-177">Question {step} of 3</span>
              <span data-id="element-178">{Math.round(step / 3 * 100)}%</span>
            </div>
            <div className="w-full bg-muted rounded-full h-1.5" data-id="element-179">
              <div className="bg-primary h-1.5 rounded-full transition-all duration-300" style={{
            width: `${step / 3 * 100}%`
          }} data-id="element-180" />
            </div>
          </div>}
      </div>
    </div>;
}